import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { listAllShows, approveShow, rejectShow, type AdminShowSummary } from '@/lib/api/admin'

type ShowStatus = 'pending' | 'published' | 'rejected'

interface ShowRow {
  id: string
  title: string
  creator: string
  submittedAt: string
  status: ShowStatus
}

const tabs: { id: ShowStatus | 'all'; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'published', label: 'Published' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
]

export function AdminContent() {
  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]['id']>('pending')
  const [shows, setShows] = useState<ShowRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listAllShows()
      .then((items: AdminShowSummary[]) => {
        if (cancelled) return
        setShows(
          items.map((s) => ({
            id: s.id,
            title: s.title,
            creator: s.creator,
            submittedAt: s.submittedAt,
            status: s.status,
          })),
        )
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load shows')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const filteredShows =
    activeTab === 'all' ? shows : shows.filter((show) => show.status === activeTab)

  const updateStatus = async (id: string, status: ShowStatus) => {
    if (status === 'published') {
      const ok = await approveShow(id)
      if (!ok) return
    } else if (status === 'rejected') {
      const ok = await rejectShow(id)
      if (!ok) return
    }
    setShows((current) => current.map((show) => (show.id === id ? { ...show, status } : show)))
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Content approvals</h1>
          <p className="mt-1 text-sm text-gray-400">
            Review and approve new vertical dramas from creators. This view is powered by the real
            admin series APIs.
          </p>
        </div>
      </div>

      <div className="inline-flex rounded-full bg-white/5 p-1 text-xs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1 rounded-full transition-colors ${
              activeTab === tab.id ? 'bg-[#D4AF37] text-black' : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-800 bg-black/40">
        <div className="grid grid-cols-[2fr_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 border-b border-gray-800">
          <div>Show</div>
          <div>Creator</div>
          <div>Submitted</div>
          <div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-400">Loading shows…</div>
        ) : filteredShows.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400">
            No shows in this state yet. Once creators start submitting vertical series, they will
            appear here for review.
          </div>
        ) : (
          <div className="divide-y divide-gray-900">
            {filteredShows.map((show) => (
              <div
                key={show.id}
                className="grid grid-cols-[2fr_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-4 py-3 text-sm items-center group"
              >
                <Link
                  to={`/admin/content/${show.id}`}
                  className="hover:bg-white/5 -mx-4 px-4 py-3 rounded-lg transition-colors block"
                >
                  <p className="font-medium text-white group-hover:text-[#D4AF37]">{show.title}</p>
                  <p className="text-xs text-gray-500 capitalize">Status: {show.status}</p>
                </Link>
                <div className="text-gray-300">{show.creator}</div>
                <div className="text-gray-400 text-xs">
                  {show.submittedAt
                    ? new Date(show.submittedAt).toLocaleDateString()
                    : '—'}
                </div>
                <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  <Link to={`/admin/content/${show.id}`}>
                    <Button
                      size="xs"
                      variant="secondary"
                      className="border-gray-800 text-gray-300 hover:bg-white/5"
                    >
                      View / Edit
                    </Button>
                  </Link>
                  {show.status !== 'published' && (
                    <Button
                      size="xs"
                      className="bg-emerald-600 hover:bg-emerald-600/90 text-white"
                      onClick={() => updateStatus(show.id, 'published')}
                    >
                      Approve
                    </Button>
                  )}
                  {show.status !== 'rejected' && (
                    <Button
                      size="xs"
                      variant="secondary"
                      className="border-red-800 text-red-400 hover:bg-red-900/20"
                      onClick={() => updateStatus(show.id, 'rejected')}
                    >
                      Reject
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <p className="text-[11px] text-red-300">
          {error}
        </p>
      )}
    </div>
  )
}

